import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Trash2, CheckCircle2, Sparkles, AlertCircle } from "lucide-react";
import { BUSINESS_SNAPSHOT_QUESTIONS } from "../../lib/businessSnapshotQA";

// Qualitative Business Snapshot Q&A — rendered INSIDE the Business Snapshot
// card when the founder hasn't uploaded a pitch deck. The business website is
// captured by the card's own Website field, so this sub-section only offers
// the company-profile upload. Answers feed the CRS analysis and are visible
// to the admin team.
export const BusinessSnapshotQA = ({ profileDocs = [], uploadingState, onUpload, onRemove }) => {
  const [answers, setAnswers] = useState({});
  const [savedAnswers, setSavedAnswers] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);

  // Unsaved edits = any question whose local text differs from the last save.
  const dirty = BUSINESS_SNAPSHOT_QUESTIONS.some(
    (q) => (answers[q.key] || "").trim() !== (savedAnswers[q.key] || "").trim());
  // Let the onboarding wizard know so "Next" can insist on Save and Continue.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("clinq:snapshot-qa-dirty", { detail: { dirty } }));
    return () => window.dispatchEvent(new CustomEvent("clinq:snapshot-qa-dirty", { detail: { dirty: false } }));
  }, [dirty]);

  const aiDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/companies/me/business-snapshot-qa/ai-draft");
      const drafts = data?.answers || {};
      let filled = 0;
      const next = { ...answers };
      for (const q of BUSINESS_SNAPSHOT_QUESTIONS) {
        if (!(next[q.key] || "").trim() && (drafts[q.key] || "").trim()) {
          next[q.key] = drafts[q.key].trim();
          filled += 1;
        }
      }
      setAnswers(next);
      if (filled > 0) {
        toast.success(`Drafted ${filled} answer${filled === 1 ? "" : "s"} from your website / profile`, {
          description: "Review and edit them, then hit Save and Continue to confirm.",
        });
      } else {
        toast.info("Nothing new to draft", {
          description: "Your answers are already filled in, or the sources didn't cover the remaining questions.",
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft answers — please try again.");
    } finally { setDrafting(false); }
  };

  useEffect(() => {
    api.get("/companies/me/business-snapshot-qa").then((r) => {
      setAnswers(r.data?.answers || {});
      setSavedAnswers(r.data?.answers || {});
      setSavedAt(r.data?.saved_at || null);
    }).catch(() => {});
  }, []);

  const setAnswer = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/companies/me/business-snapshot-qa", { answers });
      setSavedAnswers(answers);
      setSavedAt(new Date().toISOString());
      toast.success("Business snapshot saved", {
        description: "Your answers will feed directly into your Capital Readiness Score.",
      });
      document.querySelector("[data-testid='step-deep-dive']")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your answers — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="business-snapshot-qa" className="mt-6 border-t border-[#0A8B85]/20 pt-5">
      <p className="text-sm leading-relaxed text-neutral-700">
        We&apos;d love to learn more about your business. Do you have a company profile
        document or a business website we can reference? If yes, please add the link to
        the Website field above or upload the document below. We will use it to help
        complete your business snapshot. If not, no problem — simply answer the
        questions below.
      </p>

      <div className="mt-4 max-w-md">
        <label className="text-xs font-bold text-neutral-700">Company profile document <span className="font-normal text-neutral-400">(optional)</span></label>
        {uploadingState ? (
          <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-600" data-testid="biz-snapshot-profile-uploading">
            <Loader2 className="h-4 w-4 animate-spin text-[#0A8B85]" />
            <span className="truncate">{uploadingState.filename} — {uploadingState.progress}%</span>
          </div>
        ) : profileDocs.length > 0 ? (
          <div className="mt-1.5 space-y-1.5">
            {profileDocs.map((d) => (
              <div key={d.doc_id} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm" data-testid="biz-snapshot-profile-doc">
                <FileText className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="min-w-0 flex-1 truncate font-medium text-neutral-800">{d.filename}</span>
                <button type="button" onClick={() => onRemove(d.doc_id)} aria-label="Remove document"
                  data-testid="biz-snapshot-profile-remove"
                  className="rounded p-1 text-neutral-400 hover:bg-white hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-600 hover:border-[#0A8B85] hover:text-[#0A8B85]" data-testid="biz-snapshot-profile-upload">
            <Upload className="h-4 w-4" />
            <span>Upload company profile (PDF / DOC)</span>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.ppt,.pptx"
              data-testid="biz-snapshot-profile-file"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
          </label>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" onClick={aiDraft} disabled={drafting}
          data-testid="biz-snapshot-ai-draft-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-[#0A8B85] bg-white px-4 py-2 text-xs font-bold text-[#0A8B85] hover:bg-[#0A8B85]/5 disabled:opacity-50">
          {drafting
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading your website / profile…</>
            : <><Sparkles className="h-3.5 w-3.5" /> Draft my answers for me</>}
        </button>
        <span className="text-[11px] text-neutral-500">
          Uses your website or company profile to pre-fill unanswered questions — you confirm before saving.
        </span>
      </div>

      <div className="mt-6 space-y-5">
        {BUSINESS_SNAPSHOT_QUESTIONS.map((q, i) => (
          <div key={q.key}>
            <label className="text-sm font-bold text-neutral-900">
              {i + 1}. {q.label}
            </label>
            <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">{q.helper}</p>
            <textarea
              rows={3}
              value={answers[q.key] || ""}
              onChange={(e) => setAnswer(q.key, e.target.value)}
              data-testid={`biz-snapshot-q-${q.key}`}
              className="mt-2 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm leading-relaxed outline-none focus:border-[#0A8B85]"
            />
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid="biz-snapshot-save-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50"
        >
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save and Continue"}
        </button>
        {dirty ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700" data-testid="biz-snapshot-unsaved-badge">
            <AlertCircle className="h-3.5 w-3.5" /> Unsaved changes — click Save and Continue before moving on
          </span>
        ) : savedAt && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="biz-snapshot-saved-badge">
            <CheckCircle2 className="h-3.5 w-3.5" /> Saved — you can update your answers any time
          </span>
        )}
      </div>
    </div>
  );
};
